import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { AlertCard, AlertCardBody, getAlertCardColors } from '../../../../common/cards/alert-card/AlertCard';

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: theme.spacing(4),
    maxWidth: '1000px',
    margin: '0 auto',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  table: {
    marginTop: theme.spacing(3),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(4),
  },
  button: {
    borderRadius: '6px',
    height: '38px',
    fontSize: '14px',
    textTransform: 'capitalize',
    alignSelf: 'start',
  },
  summaryBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  summaryText: {
    fontSize: '16px',
    fontWeight: 600,
  },
}));

interface Props {
  onDeactivate: (volunteers: any) => void;
  onBack: () => void;
  data: any[];
  setData: (data: any[]) => void;
  isDeactivating: boolean;
}

export function ReviewStep({ onDeactivate, onBack, data, isDeactivating }: Props) {
  const classes = useStyles();
  const { darkTextColor, lightTextColor } = getAlertCardColors('warning');

  const validEmails = data.filter((row) => row.email && row.email.trim() !== '');

  const handleDeactivate = () => {
    const volunteers = validEmails.map((row) => ({
      email: row.email.trim(),
    }));
    onDeactivate(volunteers);
  };

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>Review Volunteers to Deactivate</Typography>
      <Typography className={classes.subtitle}>
        Please review the list of volunteers that will be deactivated. This action will retire these volunteers and send
        notifications to configured recipients.
      </Typography>

      <Box className={classes.summaryBox}>
        <Typography className={classes.summaryText}>Total volunteers to deactivate: {validEmails.length}</Typography>
      </Box>

      <Box mt={2}>
        <AlertCard variant='warning' title='Final Warning'>
          <AlertCardBody>
            <Typography variant='subtitle2' style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}>
              You are about to deactivate{' '}
              <span style={{ fontWeight: 'bold', color: darkTextColor }}>{validEmails.length}</span> volunteer
              {validEmails.length !== 1 ? 's' : ''}. This action will remove them from all active enrollments and
              rosters. Notifications will be sent to configured recipients.
            </Typography>
          </AlertCardBody>
        </AlertCard>
      </Box>

      <TableContainer component={Paper} className={classes.table}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Email Address</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validEmails.map((row, index) => (
              <TableRow key={row.email}>
                <TableCell>{index + 1}</TableCell>
                <TableCell>{row.email}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className={classes.buttonContainer}>
        <Button classes={{ root: classes.button }} variant='outlined' onClick={onBack} disabled={isDeactivating}>
          Back
        </Button>
        <Button
          classes={{ root: classes.button }}
          variant='contained'
          color='secondary'
          onClick={handleDeactivate}
          disabled={isDeactivating || validEmails.length === 0}
        >
          {isDeactivating
            ? 'Deactivating...'
            : `Deactivate ${validEmails.length} Volunteer${validEmails.length !== 1 ? 's' : ''}`}
        </Button>
      </Box>
    </Box>
  );
}
